import { useState, useEffect } from "react";
import { collection, getDocs, getDoc, doc, setDoc, query, where } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { db, auth } from "../firebase";
import AppCard from "./AppCard";
import { loadMetrics } from "../services/metrics";

const FONT = "'Plus Jakarta Sans', -apple-system, sans-serif";

function useIsMobile() {
  const [m, setM] = useState(
    typeof window !== "undefined" && window.innerWidth < 768,
  );
  useEffect(() => {
    const h = () => setM(window.innerWidth < 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return m;
}

function Isotipo({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" style={{ flexShrink: 0 }}>
      <line x1="20" y1="5"  x2="20" y2="20" stroke="#D1D5DB" strokeWidth="2"/>
      <line x1="35" y1="20" x2="20" y2="20" stroke="#D1D5DB" strokeWidth="2"/>
      <line x1="20" y1="35" x2="20" y2="20" stroke="#D1D5DB" strokeWidth="2"/>
      <line x1="5"  y1="20" x2="20" y2="20" stroke="#D1D5DB" strokeWidth="2"/>
      <circle cx="20" cy="5"  r="3" fill="#E03131"/>
      <circle cx="35" cy="20" r="3" fill="#E03131"/>
      <circle cx="20" cy="35" r="3" fill="#E03131"/>
      <circle cx="5"  cy="20" r="3" fill="#E03131"/>
      <circle cx="20" cy="20" r="5" fill="#E03131"/>
    </svg>
  );
}

function TenantLogo({ tenant, primaryColor }) {
  const [imgError, setImgError] = useState(false);
  const logoUrl     = tenant?.logoUrl     || "";
  const logoText    = tenant?.logoText    || "ObraCore";
  const logoSubtext = tenant?.logoSubtext || "";

  if (logoUrl && !imgError) {
    return (
      <img
        src={logoUrl}
        alt={logoText}
        onError={() => setImgError(true)}
        style={{ height: 36, maxWidth: 180, objectFit: "contain", objectPosition: "left" }}
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: primaryColor,
        letterSpacing: "0.05em", lineHeight: 1 }}>
        {logoText}
      </div>
      {logoSubtext && (
        <div style={{ fontSize: 9, color: primaryColor, opacity: 0.65,
          letterSpacing: "0.15em", textTransform: "uppercase" }}>
          {logoSubtext}
        </div>
      )}
    </div>
  );
}

export default function LauncherPage({ user }) {
  const [apps, setApps]               = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [tenant, setTenant]           = useState(null);
  const [loading, setLoading]         = useState(true);
  const [noAccess, setNoAccess]       = useState(false);
  const [metrics, setMetrics]         = useState({});
  const [misEmpresas, setMisEmpresas] = useState([]);
  const [cambiando, setCambiando]     = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    async function loadData() {
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (!userDoc.exists()) { setNoAccess(true); setLoading(false); return; }
        const profile = { id: userDoc.id, ...userDoc.data() };
        setUserProfile(profile);
        // Empresa ACTIVA (multi-empresa). Fallback a la principal.
        const tenantId = profile.active_tenant_id || profile.tenant_id;

        const tenantDoc = await getDoc(doc(db, "tenants", tenantId));
        if (tenantDoc.exists()) setTenant(tenantDoc.data());

        // Si pertenece a más de una empresa, cargar los nombres para el selector
        const idsEmpresas = profile.tenant_ids || [profile.tenant_id];
        if (idsEmpresas.length > 1) {
          const snaps = await Promise.all(
            idsEmpresas.map((t) => getDoc(doc(db, "tenants", t)))
          );
          setMisEmpresas(
            snaps
              .filter((snap) => snap.exists())
              .map((snap) => ({ id: snap.id, ...snap.data() }))
          );
        }

        const appsSnapshot = await getDocs(collection(db, "apps"));
        const appsData = {};
        appsSnapshot.docs.forEach(d => { appsData[d.id] = { id: d.id, ...d.data() }; });

        if (profile.role_platform === "superadmin") {
          const tenantAppsQuery = query(
            collection(db, "tenant_apps"),
            where("tenant_id", "==", tenantId),
            where("enabled", "==", true)
          );
          const tenantAppsSnap = await getDocs(tenantAppsQuery);
          const tenantAppIds = tenantAppsSnap.docs.map(d => d.data().app_id);
          const appsToShow = tenantAppIds
            .map(appId => appsData[appId])
            .filter(Boolean)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
          setApps(appsToShow);
          try { const m = await loadMetrics(tenantId); setMetrics(m); } catch(e) {}
          setLoading(false);
          return;
        }

        const rolesQuery = query(
          collection(db, "user_app_roles"),
          where("user_id", "==", user.uid),
          where("tenant_id", "==", tenantId),
          where("enabled", "==", true)
        );
        const rolesSnapshot = await getDocs(rolesQuery);

        if (rolesSnapshot.empty) { setNoAccess(true); setLoading(false); return; }

        const tenantAppsQuery = query(
          collection(db, "tenant_apps"),
          where("tenant_id", "==", tenantId),
          where("enabled", "==", true)
        );
        const tenantAppsSnap = await getDocs(tenantAppsQuery);
        const tenantAppIds = tenantAppsSnap.docs.map(d => d.data().app_id);

        const userApps = rolesSnapshot.docs
          .map(d => {
            const roleData = d.data();
            if (!tenantAppIds.includes(roleData.app_id)) return null;
            const appInfo = appsData[roleData.app_id];
            if (!appInfo) return null;
            return { ...appInfo, userRole: roleData.role };
          })
          .filter(Boolean)
          .sort((a, b) => (a.order || 0) - (b.order || 0));

        setApps(userApps);
        try { const m = await loadMetrics(tenantId); setMetrics(m); } catch(e) {}
        setLoading(false);
      } catch (err) {
        console.error("LauncherPage error:", err);
        setLoading(false);
      }
    }
    loadData(); // eslint-disable-next-line
  }, [user.uid]);

  async function handleLogout() { await signOut(auth); }

  // Cambia la empresa activa. La regla de Firestore solo lo permite si la
  // empresa está en tenant_ids, así que no se puede forzar desde el navegador.
  // Recarga la página para que todo (branding, apps, métricas) se relea limpio.
  async function cambiarEmpresa(nuevoTenantId) {
    const actual = userProfile?.active_tenant_id || userProfile?.tenant_id;
    if (!nuevoTenantId || nuevoTenantId === actual) return;
    setCambiando(true);
    try {
      await setDoc(
        doc(db, "users", user.uid),
        { active_tenant_id: nuevoTenantId },
        { merge: true }
      );
      window.location.reload();
    } catch (e) {
      console.error("Error cambiando de empresa:", e);
      alert("No se pudo cambiar de empresa: " + e.message);
      setCambiando(false);
    }
  }

  if (loading) return (
    <div style={sx.centered}>
      <p style={{ color: "#9CA3AF", fontFamily: FONT, fontSize: 14 }}>Cargando...</p>
    </div>
  );

  if (noAccess) return (
    <div style={sx.centered}>
      <div style={sx.noAccessCard}>
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF"
          strokeWidth="2" strokeLinecap="round" style={{ marginBottom: 16 }}>
          <rect x="3" y="11" width="18" height="11" rx="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
        <h2 style={sx.noAccessTitle}>Tu cuenta está siendo configurada</h2>
        <p style={sx.noAccessText}>El administrador de tu empresa todavía no te asignó acceso a ninguna aplicación.</p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 24 }}>
          <button onClick={() => window.location.reload()} style={sx.retryBtn}>Reintentar</button>
          <button onClick={handleLogout} style={sx.logoutBtnAlt}>Cerrar sesión</button>
        </div>
      </div>
    </div>
  );

  const primaryColor = tenant?.primaryColor || "#E03131";
  const displayName  = userProfile?.display_name || user.email;
  const initials     = displayName.split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase();
  const firstName    = displayName.split(" ")[0];
  const roleLabel =
    userProfile?.role_platform === "superadmin"
      ? "Super Admin"
      : userProfile?.role_platform === "tenant_admin"
        ? "Administrador"
        : "Usuario";

  const hour      = new Date().getHours();
  const saludo    = hour < 12 ? "Buenos días" : hour < 18 ? "Buenas tardes" : "Buenas noches";
  const fecha     = new Date().toLocaleDateString("es-AR", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  const fechaCap  = fecha.charAt(0).toUpperCase() + fecha.slice(1);

  return (
    <div
      style={{ minHeight: "100vh", background: "#F7F9FC", fontFamily: FONT }}
    >
      {/* HEADER */}
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 16px",
          height: "auto",
          minHeight: 56,
          flexWrap: "wrap",
          gap: 8,
          background: "#FFFFFF",
          borderBottom: "0.5px solid #E5EAF2",
          borderTop: `3px solid ${primaryColor}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <TenantLogo tenant={tenant} primaryColor={primaryColor} />
          <div style={{ width: 1, height: 28, background: "#E5EAF2" }} />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              border: "0.5px solid #E5EAF2",
              borderRadius: 20,
              padding: "4px 10px 4px 7px",
            }}
          >
            <Isotipo size={16} />
            <span style={{ fontSize: 10, color: "#9CA3AF" }}>
              Operando con{" "}
              <span style={{ color: "#1F2937", fontWeight: 600 }}>Obra</span>
              <span style={{ color: "#E03131", fontWeight: 700 }}>Core</span>
            </span>
          </div>

          {/* Selector de empresa activa — solo si el usuario tiene más de una */}
          {misEmpresas.length > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 1, height: 28, background: "#E5EAF2" }} />
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9CA3AF"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M3 21h18" />
                <path d="M5 21V7l8-4v18" />
                <path d="M19 21V11l-6-4" />
              </svg>
              <select
                value={userProfile?.active_tenant_id || userProfile?.tenant_id || ""}
                onChange={(e) => cambiarEmpresa(e.target.value)}
                disabled={cambiando}
                title="Empresa en la que estás operando"
                style={{
                  padding: "5px 8px",
                  border: "0.5px solid #E5EAF2",
                  borderRadius: 7,
                  background: "#FFFFFF",
                  color: "#1F2937",
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: FONT,
                  cursor: cambiando ? "wait" : "pointer",
                  maxWidth: 190,
                }}
              >
                {misEmpresas.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name || emp.logoText || emp.id}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {!isMobile && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                lineHeight: 1.25,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  color: "#4B5563",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {displayName}
              </span>
              <span
                style={{
                  fontSize: 10,
                  color: "#9CA3AF",
                  whiteSpace: "nowrap",
                }}
              >
                {[user.email, roleLabel].filter(Boolean).join("  ·  ")}
              </span>
            </div>
          )}
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: primaryColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: 11,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {initials}
          </div>
          <button
            onClick={handleLogout}
            style={{
              padding: "5px 12px",
              background: "transparent",
              border: "0.5px solid #E5EAF2",
              borderRadius: 7,
              cursor: "pointer",
              fontSize: 11,
              color: "#9CA3AF",
              fontFamily: FONT,
            }}
          >
            Salir
          </button>
        </div>
      </header>

      {/* HERO */}
      <div
        style={{ maxWidth: 760, margin: "0 auto", padding: "24px 16px 8px" }}
      >
        <div
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: "#1F2937",
            marginBottom: 3,
          }}
        >
          {saludo}, {firstName}
        </div>
        <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 28 }}>
          {fechaCap}
        </div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#9CA3AF",
            marginBottom: 14,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          Centro de control
        </div>
      </div>

      {/* MÓDULOS */}
      <div
        style={{
          maxWidth: 760,
          margin: "0 auto",
          padding: "0 16px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 12,
        }}
      >
        {apps.map((app) => (
          <AppCard
            key={app.id}
            app={app}
            disabled={false}
            metrics={metrics[app.icon]}
          />
        ))}
      </div>

      {/* FOOTER */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 7,
          padding: "48px 28px 28px",
        }}
      >
        <Isotipo size={14} />
        <span style={{ fontSize: 11, color: "#9CA3AF", opacity: 0.6 }}>
          Obra<span style={{ color: "#E03131", fontWeight: 700 }}>Core</span> ·
          Plataforma de gestión integral
        </span>
      </div>
    </div>
  );
}

const sx = {
  centered: { minHeight: "100vh", display: "flex", alignItems: "center",
    justifyContent: "center", background: "#F7F9FC" },
  noAccessCard: {
    background: "#FFFFFF", borderRadius: 16, padding: "48px 40px",
    maxWidth: 420, textAlign: "center",
    border: "0.5px solid #E5EAF2",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  noAccessTitle: { fontSize: 17, fontWeight: 700, color: "#1F2937", marginBottom: 8 },
  noAccessText: { fontSize: 13, color: "#4B5563", lineHeight: 1.6 },
  retryBtn: {
    padding: "9px 22px", background: "#E03131", color: "white",
    border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13,
    fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  logoutBtnAlt: {
    padding: "9px 22px", background: "transparent", color: "#4B5563",
    border: "0.5px solid #E5EAF2", borderRadius: 8, cursor: "pointer",
    fontSize: 13, fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
};